
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import { _ as _objectSpread } from './_rollupPluginBabelHelpers-mixin.js';
import Sortable from '@shopify/draggable/lib/es5/sortable.js';

const onMirrorCreated = Symbol('onMirrorCreated');
const onMirrorDestroy = Symbol('onMirrorDestroy');
const defaultOptions = {
  duration: 100,
  easingFunction: 'ease-in-out'
};

function resetElementOnTransitionEnd({
  mirror,
  original,
  classes
}) {
  if (mirror && mirror.parentNode) {
    mirror.removeEventListener('transitionend', resetElementOnTransitionEnd);
    mirror.parentNode.removeChild(mirror);
  }

  original.classList.remove(classes['source:dragging']);
}

function animate(mirror, original, {
  x,
  y
}, {
  duration,
  easingFunction
}, classes) {
  original.classList.add(classes['source:dragging']);

  const _DOMMatrix = new DOMMatrix(mirror.style.transform),
        mirrorX = _DOMMatrix.m41,
        mirrorY = _DOMMatrix.m42;

  const distanceToTravel = Math.round(Math.hypot(mirrorX - x, mirrorY - y) * 0.8);
  duration = Math.max(distanceToTravel, 80);
  requestAnimationFrame(() => {
    mirror.addEventListener('transitionend', resetElementOnTransitionEnd.bind(this, {
      mirror,
      original,
      classes
    }));
    mirror.style.transition = `transform ${duration}ms ${easingFunction}, box-shadow ${duration}ms ${easingFunction}`;
    mirror.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    mirror.style.boxShadow = '0px 0px 0px rgba(0, 0, 0, 0.17)';
  });
}

class PlaceAnimation {
  constructor(draggable) {
    this.draggable = draggable;
    this.options = _objectSpread({}, defaultOptions, this.getOptions());
    this.classes = _objectSpread({}, this.draggable.options.classes);
    this.lastAnimationFrame = null;
    this[onMirrorDestroy] = this[onMirrorDestroy].bind(this);
    this[onMirrorCreated] = this[onMirrorCreated].bind(this);
  }

  attach() {
    this.draggable.on('mirror:created', this[onMirrorCreated]).on('mirror:destroy', this[onMirrorDestroy]);
  }

  detach() {
    this.draggable.off('mirror:created', this[onMirrorCreated]).off('mirror:destroy', this[onMirrorDestroy]);
  }

  getOptions() {
    return this.draggable.options.placeAnimation || {};
  }

  [onMirrorCreated]({
    mirror
  }) {
    requestAnimationFrame(() => {
      mirror.style.transition = `box-shadow ${this.options.duration}ms ${this.options.easingFunction}`;
      mirror.style.boxShadow = '0px 10px 16px rgba(0, 0, 0, 0.17)';
    });
  }

  [onMirrorDestroy](event) {
    event.cancel();
    const _event$data = event.data,
          _event$data$dragEvent = _event$data.dragEvent.data,
          originalSource = _event$data$dragEvent.originalSource,
          source = _event$data$dragEvent.source,
          mirror = _event$data.mirror;

    const _source$getBoundingCl = source.getBoundingClientRect(),
          x = _source$getBoundingCl.x,
          y = _source$getBoundingCl.y;

    cancelAnimationFrame(this.lastAnimationFrame);
    this.lastAnimationFrame = requestAnimationFrame(() => {
      animate(mirror, originalSource, {
        x,
        y
      }, this.options, this.classes);
    });
  }

}

var script = {
  name: 'Draggable'
};

const __vue_script__ = script;
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = undefined;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "draggable.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

__vue_normalize__({}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

const DRAGGABLE_DEFAULTS = {
  containers: '[pendo-draggable-container]',
  item: '[pendo-draggable-item]',
  handle: '[pendo-draggable-handle]',
  mirror: {
    constrainDimensions: true
  },
  placeAnimation: {
    easingFunction: 'ease-in-out'
  },
  classes: {
    mirror: 'pendo-draggable-mirror',
    'body:dragging': 'pendo-draggable--is-dragging',
    'draggable:over': 'pendo-draggable--over',
    'container:dragging': 'pendo-draggable-container--is-dragging',
    'container:over': 'pendo-draggable-container--over',
    'container:placed': 'pendo-draggable-container--placed',
    'source:dragging': 'pendo-draggable-source--is-dragging',
    'source:original': 'pendo-draggable--original',
    'source:placed': 'pendo-draggable-source--placed'
  }
};
function getOptions(el, options) {
  return _objectSpread({
    classes: _objectSpread({}, PendoDraggable.options.classes, options.classes),
    draggable: options.item || PendoDraggable.options.item
  }, options.handle && {
    handle: options.handle
  }, el.querySelectorAll(PendoDraggable.options.handle).length && {
    handle: PendoDraggable.options.handle
  }, {
    containers: PendoDraggable.options.containers || options.containers,
    mirror: _objectSpread({}, PendoDraggable.options.mirror, options.mirror)
  }, options.plugins && {
    plugins: options.plugins
  }, {
    disabled: options.disabled
  });
}
function getDraggableStatus(el, draggable) {
  const hasActiveContainers = !!el.querySelectorAll('[pendo-draggable-container]').length;
  const hasActiveItems = !!el.querySelectorAll('[pendo-draggable-item]').length;
  const hasActiveHandles = !!el.querySelectorAll('[pendo-draggable-handle]').length;

  if (draggable.containers.length === 1) {
    if (draggable.options.handle) {
      return hasActiveItems && hasActiveHandles;
    }

    return hasActiveItems;
  }

  if (draggable.options.handle) {
    return hasActiveContainers && hasActiveItems && hasActiveHandles;
  }

  return hasActiveContainers && hasActiveItems;
}
function shouldCancelDrag(element) {
  let currentElem = element;
  let isParent = false;

  while (currentElem) {
    const hasAttribute = currentElem.hasAttribute('pendo-draggable-ignore');

    if (hasAttribute) {
      isParent = true;
      currentElem = undefined;
    } else {
      currentElem = currentElem.parentElement;
    }
  }

  return isParent;
}
function registerEventListeners(draggable, {
  events
} = {}) {
  if (!events) {
    return;
  }

  if (Object.keys(events)) {
    Object.entries(events).forEach(([event, fn]) => {
      draggable.on(event, fn);
    });
  }
}
function createDraggable(el, {
  value
}) {
  const options = _objectSpread({}, getOptions(el, _objectSpread({}, value)), {
    plugins: [PlaceAnimation]
  });

  let containers = el.querySelectorAll(options.containers);

  if (containers.length === 0) {
    containers = el;
  }

  const hasIgnoredSelectors = !!el.querySelectorAll('[pendo-draggable-ignore]').length;
  const draggable = new Sortable(containers, _objectSpread({}, options, {
    hasIgnoredSelectors
  }));
  return draggable;
}
function inserted(el, binding, vnode) {
  const draggable = createDraggable(el, binding);
  el.classList.add('pendo-draggable');

  if (draggable.options.handle) {
    el.classList.add('has-draggable-handles');
  } else {
    el.classList.add('has-draggable-items');
  }

  el.classList.toggle('is-draggable', getDraggableStatus(el, draggable));
  registerEventListeners(draggable, binding.value);
  draggable.on('drag:start', event => {
    const isDraggable = getDraggableStatus(el, draggable);

    if (draggable.disabled || !isDraggable) {
      event.cancel();
      return;
    }

    const ignoredSelectors = !!el.querySelectorAll('[pendo-draggable-ignore]').length;
    if (!ignoredSelectors) return;
    const target = event.originalEvent.target;
    const cancelDragEvent = shouldCancelDrag(target);

    if (cancelDragEvent) {
      event.cancel();
    }
  });
  const vm = vnode.context;

  if (!vm.PendoDraggable) {
    vm.PendoDraggable = draggable;
  }
}
function componentUpdated(el, binding, vnode) {
  const PendoDraggable = vnode.context.PendoDraggable;
  el.classList.toggle('is-draggable', getDraggableStatus(el, PendoDraggable));
}
function unbind(el, binding, vnode) {
  if (vnode.context.PendoDraggable) {
    vnode.context.PendoDraggable.destroy();
    delete vnode.context.PendoDraggable;
  }
}
const PendoDraggable = {
  options: DRAGGABLE_DEFAULTS,
  inserted,
  componentUpdated,
  unbind
};

export default PendoDraggable;
export { DRAGGABLE_DEFAULTS, PendoDraggable, componentUpdated, createDraggable, getDraggableStatus, getOptions, inserted, registerEventListeners, shouldCancelDrag, unbind };
